import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { getAllSupportRequests, markSupportRequestSeen } from "../../../../Services/VersionsApi";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import GetAppIcon from "@mui/icons-material/GetApp";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const NewVersionRequestTap = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | new | read

  const handleMarkSeen = useCallback(async (id) => {
    if (!id) return;
    try {
      setProcessingId(id);
      await markSupportRequestSeen(id);
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, seen: 1 } : r)));
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "فشل تعليم الطلب كمقروء");
    } finally {
      setProcessingId(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAllSupportRequests()
      .then((res) => {
        const data = res?.data?.data || res?.data || [];
        if (mounted) setRequests(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err?.message || "تعذر جلب الطلبات");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // بيانات مفلترة حسب البحث ونطاق التاريخ (created_at) والحالة
  const filteredRequests = (Array.isArray(requests) ? requests : []).filter((r) => {
    const q = (searchQuery || "").toLowerCase().trim();
    // بحث نصي
    const textMatch = (() => {
      if (!q) return true;
      const userName = (r.user?.name || "").toLowerCase();
      const userEmail = (r.user?.email || "").toLowerCase();
      const userPhone = (r.user?.phone || "").toLowerCase();
      const planeName = (r.plane?.plane_name || "").toLowerCase();
      const message = (r.message || "").toLowerCase();
      return (
        userName.includes(q) ||
        userEmail.includes(q) ||
        userPhone.includes(q) ||
        planeName.includes(q) ||
        message.includes(q) ||
        String(r.id).includes(q)
      );
    })();

    if (!textMatch) return false;

    // فلترة بنطاق التاريخ
    if (startDate || endDate) {
      const rDate = r.created_at ? new Date(r.created_at) : null;
      if (!rDate) return false;
      if (startDate) {
        const sd = new Date(startDate);
        sd.setHours(0,0,0,0);
        if (rDate < sd) return false;
      }
      if (endDate) {
        const ed = new Date(endDate);
        ed.setHours(23,59,59,999);
        if (rDate > ed) return false;
      }
    }

    // فلترة حسب حالة القراءة (seen)
    if (statusFilter === "new" && Number(r.seen)) return false;
    if (statusFilter === "read" && !Number(r.seen)) return false;

    return true;
  });

  // دالة تصدير البيانات إلى CSV (يفتح في Excel)
  const exportToCSV = () => {
    const rows = filteredRequests;
    if (!rows.length) {
      alert("لا توجد بيانات للتصدير");
      return;
    }

    const headers = [
      "ID",
      "اسم العميل",
      "بريد العميل",
      "هاتف العميل",
      "اسم الباقة",
      "وصف الباقة",
      "السعر",
      "الرسالة",
      "التاريخ",
      "الحالة"
    ];

    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const lines = rows.map((r) => {
      const userName = r.user?.name || "";
      const userEmail = r.user?.email || "";
      const userPhone = r.user?.phone || "";
      const planeName = r.plane?.plane_name || "";
      const planeDesc = r.plane?.desc || "";
      const planePrice = r.plane?.price ?? "";
      const message = r.message || "";
      const date = r.created_at ? new Date(r.created_at).toISOString() : "";
      const status = Number(r.seen) ? "مقروء" : "جديد";

      return [
        escape(r.id),
        escape(userName),
        escape(userEmail),
        escape(userPhone),
        escape(planeName),
        escape(planeDesc),
        escape(planePrice),
        escape(message),
        escape(date),
        escape(status)
      ].join(",");
    });

    const csvContent = "\uFEFF" + headers.join(",") + "\n" + lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `new_version_requests_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!requests.length) {
    return <Typography color="text.secondary">لا توجد طلبات دعم حالياً</Typography>;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>طلبات الحصول على نسخ جديدة</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="بحث بالاسم، البريد، الهاتف، الباقة، أو الرسالة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 320 }}
          />
          <TextField
            size="small"
            type="date"
            label="من"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="إلى"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            select
            label="الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 140 }}
          >
            <MenuItem value="all">الكل</MenuItem>
            <MenuItem value="new">جديد</MenuItem>
            <MenuItem value="read">مقروء</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={exportToCSV} size="small">
            تصدير Excel
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>العميل</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الباقة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الرسالة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>التاريخ</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {r.user ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{r.user.name || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{r.user.email || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{r.user.phone || "-"}</Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {r.plane ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {r.plane.plane_name || "-"}
                      </Typography>
                      {r.plane.desc ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 240, whiteSpace: 'normal' }}>
                          {r.plane.desc}
                        </Typography>
                      ) : null}
                      {typeof r.plane.price !== "undefined" && r.plane.price !== null ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          السعر: {r.plane.price} {r.plane.yearly_price || r.plane.monthly_price ? '' : ''}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.message || "-"}</TableCell>
                <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</TableCell>
                <TableCell>
                  <Chip
                    label={Number(r.seen) ? "مقروء" : "جديد"}
                    color={Number(r.seen) ? "default" : "primary"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color={Number(r.seen) ? "success" : "info"}
                    title={Number(r.seen) ? "مقروء" : "وضع كمقروء"}
                    onClick={() => handleMarkSeen(r.id)}
                    disabled={processingId === r.id}
                  >
                    <MarkEmailReadIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NewVersionRequestTap;
